// src/services/subscription.js
import { api } from "./api";

/**
 * Démarre un paiement d'abonnement.
 * @param {Object} params
 * @param {string} params.planCode - Code du plan (ex: "OWNER_PRO")
 * @param {string} [params.provider="fedapay"] - Fournisseur de paiement
 * @param {string} [params.callbackUrl] - (optionnel) URL de retour front
 * @returns {Promise<{redirect_url?: string, reference?: string, payment_url?: string, ...}>}
 */

/** Démarrer un paiement d'abonnement (owner/vendor) */
export async function startSubscription({ planCode, provider = "fedapay" }) {
  const { data } = await api.post("/checkout/subscription", {
    plan_code: planCode,
    provider,
  });
  // { payment_id, transaction_id, checkout_url }
  return data;
}

/** Démarrer un paiement de tenant-pass (locataire) */
export async function startTenantPass({ provider = "fedapay" } = {}) {
  const { data } = await api.post("/checkout/tenant-pass", { provider });
  // { payment_id, transaction_id, checkout_url }
  return data;
}

/** 
 * Confirmer côté backend après retour FedaPay (?id=...) 
 * 🆕 Redirige automatiquement vers le profil si paiement réussi
 * 🆕 Affiche un toast esthétique de confirmation ou d'erreur
 */
export async function confirmPayment(transactionId, { redirectOnSuccess = true } = {}) {
  try {
    const { data } = await api.post("/checkout/confirm", {
      transaction_id: String(transactionId),
    });
    
    // 🆕 Si paiement réussi : toast vert + redirection
    if (data.status === "succeeded") {
      const bonusMsg = data.welcome_bonus?.applied
        ? "Paiement réussi ! Vous avez reçu 2 mois gratuits pour votre premier abonnement."
        : "Paiement réussi ! Votre compte a été activé avec succès.";
      showSuccessToast(bonusMsg);

      if (redirectOnSuccess) {
        const tid = encodeURIComponent(String(transactionId));
        setTimeout(() => {
          window.location.href = `/abonnement/succes?transaction_id=${tid}`;
        }, 1500);
      }
    } 
    // 🆕 Si paiement échoué : toast rouge sans redirection
    else if (data.status === "failed") {
      console.error("❌ Paiement échoué");
      showErrorToast("Paiement échoué. Veuillez réessayer ou contacter le support.");
    }
    // Si en attente
    else if (data.status === "pending") {
      console.log("⏳ Paiement en attente de confirmation...");
      showWarningToast("Paiement en cours de traitement. Veuillez patienter...");
    }
    
    return data; // { status: 'pending'|'succeeded'|'failed' }
  } catch (e) {
    // 🆕 Afficher un toast d'erreur en cas d'exception
    const msg =
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      e?.message ||
      "Erreur inconnue pendant la confirmation";
    
    console.error("❌ Erreur confirmPayment:", e);
    showErrorToast(msg);
    
    throw new Error(msg);
  }
}

/**
 * Affiche un toast de succès esthétique (vert)
 * @param {string} message - Message à afficher
 */
function showSuccessToast(message) {
  showToast(message, {
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    shadowColor: "rgba(16, 185, 129, 0.3)",
    icon: `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style="flex-shrink: 0;">
        <circle cx="12" cy="12" r="10" fill="rgba(255, 255, 255, 0.2)" />
        <path d="M9 12l2 2 4-4" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    `,
    showSpinner: true,
  });
}

/**
 * Affiche un toast d'erreur esthétique (rouge)
 * @param {string} message - Message à afficher
 */
function showErrorToast(message) {
  showToast(message, {
    gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    shadowColor: "rgba(239, 68, 68, 0.3)",
    icon: `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style="flex-shrink: 0;">
        <circle cx="12" cy="12" r="10" fill="rgba(255, 255, 255, 0.2)" />
        <path d="M12 8v4M12 16h.01" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    `,
    showSpinner: false,
  });
}

/**
 * Affiche un toast d'avertissement esthétique (orange)
 * @param {string} message - Message à afficher
 */
function showWarningToast(message) {
  showToast(message, {
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    shadowColor: "rgba(245, 158, 11, 0.3)",
    icon: `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style="flex-shrink: 0;">
        <circle cx="12" cy="12" r="10" fill="rgba(255, 255, 255, 0.2)" />
        <path d="M12 8v4M12 16h.01" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    `,
    showSpinner: true,
  });
}

/**
 * Fonction générique pour afficher un toast
 * @param {string} message - Message à afficher
 * @param {Object} options - Options de style
 */
function showToast(message, options = {}) {
  const {
    gradient = "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    shadowColor = "rgba(16, 185, 129, 0.3)",
    icon = "",
    showSpinner = false,
  } = options;

  // Créer le conteneur du toast
  const toast = document.createElement("div");
  toast.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      background: ${gradient};
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      box-shadow: 0 10px 40px ${shadowColor}, 0 0 0 1px rgba(255, 255, 255, 0.1);
      display: flex;
      align-items: center;
      gap: 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 15px;
      font-weight: 500;
      animation: slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      max-width: 400px;
      backdrop-filter: blur(10px);
    ">
      ${icon}
      
      <!-- Message -->
      <div style="flex: 1; line-height: 1.5;">
        ${message}
      </div>
      
      ${showSpinner ? `
        <!-- Animation de chargement -->
        <div style="
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          flex-shrink: 0;
        "></div>
      ` : ""}
    </div>
    
    <style>
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateX(100px) scale(0.9);
        }
        to {
          opacity: 1;
          transform: translateX(0) scale(1);
        }
      }
      
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      @media (max-width: 640px) {
        div[style*="position: fixed"] {
          top: 10px !important;
          right: 10px !important;
          left: 10px !important;
          max-width: none !important;
        }
      }
    </style>
  `;
  
  document.body.appendChild(toast);
  
  // Retirer le toast après 4 secondes (plus long pour les erreurs)
  const duration = showSpinner ? 3000 : 4000;
  setTimeout(() => {
    toast.style.animation = "slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) reverse";
    setTimeout(() => toast.remove(), 300);
  }, duration);
}

/** Ouvrir la page de paiement renvoyée par l'API */
export function openRedirectFromResponse(res) {
  const url = res?.checkout_url || res?.payment_url || res?.redirect_url;
  if (!url) throw new Error("Aucune URL de redirection trouvée dans la réponse.");
  window.location.href = url;
}


/**
 * Récupère le statut d'un paiement/abonnement (selon ce que ton API expose).
 * Adapte l'endpoint si besoin (ex: /payments/status?reference=...).
 * @param {Object} params
 * @param {string} params.reference
 */
export async function getPaymentStatus({ reference }) {
  const { data } = await api.get(`/payments/status`, { params: { reference } });
  return data; // Attendu: { status: "pending|paid|failed", ... }
}

/**
 * Récupère l'abonnement actif de l'utilisateur connecté.
 * Utile après le retour du provider pour vérifier l'activation.
 */
/**
 * Récupère les statuts de tous les rôles de l'utilisateur
 * Nouvelle API qui retourne primary_role, roles[], et statuses{}
 */
export async function getRolesStatuses() {
  const { data } = await api.get("/me/subscription");
  console.log("getRolesStatuses data:", data);
  return data;
}

// Normalise le PASS pour le rôle tenant
export async function getMyTenantPass() {
  const data = await getRolesStatuses();
  
  // Si l'utilisateur a le rôle tenant, récupérer son statut
  if (data?.statuses?.tenant) {
    const tenantStatus = data.statuses.tenant;
    return {
      activated: tenantStatus.status === "active" && !!tenantStatus.pass,
      activated_at: tenantStatus.pass?.activated_at ?? null,
      reference: tenantStatus.pass?.reference ?? null,
    };
  }
  
  return { activated: false, activated_at: null, reference: null };
}

// Normalise l'ABO pour les rôles owner/vendor
export async function getMyActiveSubscription() {
  const data = await getRolesStatuses();
  
  // Chercher un abonnement actif parmi les rôles owner/vendor
  let activeSubscription = null;
  
  if (data?.statuses?.owner && data.statuses.owner.status === "active") {
    activeSubscription = data.statuses.owner;
  } else if (data?.statuses?.vendor && data.statuses.vendor.status === "active") {
    activeSubscription = data.statuses.vendor;
  }
  
  if (!activeSubscription || activeSubscription.kind !== "subscription") {
    return { active: false, ends_at: null, plan: null };
  }

  return {
    active: activeSubscription.status === "active" && !!activeSubscription.plan,
    ends_at: activeSubscription.expires_at ?? null,
    plan: activeSubscription.plan
      ? {
          code: activeSubscription.plan.code,
          name: activeSubscription.plan.name,
          billing_period: activeSubscription.plan.billing_period,
          price_amount: activeSubscription.plan.activePrice?.amount ?? null,
          price_currency: activeSubscription.plan.activePrice?.currency ?? "XOF",
        }
      : null,
  };
}

/**
 * Utilitaire pratique : ouvrir la page de paiement renvoyée par l'API.
 * Cherche 'redirect_url' ou 'payment_url' dans la réponse.
 */
// export function openRedirectFromResponse(res) {
//   const url = res?.redirect_url || res?.payment_url;
//   if (url) window.location.href = url;
//   else throw new Error("Aucune URL de redirection trouvée dans la réponse.");
// }